// ---------------------------------------------------------------------------
// The actual CPU-heavy work behind exportProject() (JSON.stringify + base64
// encoding of every Float64Array column + gzip) — pulled out into its own
// pure, DOM-free module so exportProjectWorker.ts (off the main thread) and
// exportProject.ts's own no-Worker fallback (Node tests, see
// exportProjectWorkerClient.ts) can both call the exact same code, instead
// of keeping two copies in sync.
//
// Real incident (2026-08-27): for a Duero-scale project (1728 sub-basins,
// ~175MB of raw series data), this whole body used to run synchronously on
// the MAIN thread inside an `async function` — being `async` only means the
// function RETURNS a promise, not that its body yields anywhere before the
// first `await` (here, the gzip step) — so the entire JSON.stringify +
// base64 encoding ran as one uninterrupted, multi-second blocking task, and
// the whole UI (including "Exportar copia"'s own dialog) froze solid for
// as long as it took. Same function, now called from inside a Worker
// (exportProjectWorker.ts) so that blocking happens on a thread nobody's
// looking at, instead of directly on the main thread.
// ---------------------------------------------------------------------------
import type { ProjectData } from "./storage.ts";

export const FORMAT = "evalhid-web-export";
export const FORMAT_VERSION = 1;

export interface Wrapper {
  format: typeof FORMAT;
  version: number;
  name: string;
  data: ProjectData;
}

// Real incident (2026-08-25): a large project's series (e.g. a MITECO
// import with hundreds of sub-basins, each with its own decades-long ROCIO
// column) crashed the tab with an out-of-memory error during export/Drive
// sync. `Array.from(value)` (turning each Float64Array into a plain number
// array) then `JSON.stringify`-ing it was the dominant cost: each 8-byte
// double becomes up to ~20 bytes of decimal text ("23.456789012345"), on
// top of the intermediate array itself — roughly a 4-5x memory peak over
// the data's own in-memory size. Encoding the column's raw bytes as base64
// instead (no per-number decimal text, no intermediate JS array — a direct
// byte-level view of the SAME buffer) keeps the expansion to base64's own
// fixed ~1.33x, regardless of how large or small the actual numbers are.
const BASE64_CHUNK_SIZE = 0x8000; // String.fromCharCode's own practical argument-count ceiling.

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK_SIZE));
  }
  return btoa(binary);
}

function base64ToFloat64Array(base64: string): Float64Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Float64Array(bytes.buffer, 0, bytes.byteLength / 8);
}

// Real incident (2026-08-25): even with the base64 fix above, `JSON.stringify`
// itself throws `RangeError: Invalid string length` for a big enough project
// (a real Duero-scale MITECO+ROCIO import, 1728 sub-basins) — `JSON.stringify`
// has to build ONE JS string holding the ENTIRE document, and every engine
// caps how long a single string can be (~1 billion UTF-16 code units in V8),
// a limit the combined base64 payload of a large-enough project's daily
// series can exceed on its own, before even accounting for the rest of the
// document. `bytesToBase64` above (each individual column) stays nowhere
// near that limit — a real Duero-scale column is at most a few hundred KB of
// base64 text — the problem is specifically the ONE-STRING document
// `JSON.stringify` builds by concatenating all of them together.
//
// The fix avoids ever building that one combined string: `chunkedReplacer`
// returns a short PLACEHOLDER token instead of the real base64 for each
// Float64Array (so `JSON.stringify` only ever has to build a document made
// of small pieces — genuinely small even for a huge project, since the
// project's own non-series fields, like `subBasins`, are only a few hundred
// bytes each), and the real (possibly large) base64 strings are collected
// SEPARATELY, into `chunks`. The small placeholder-JSON is then split back
// apart at each placeholder (`String.prototype.split` with a capturing group
// interleaves the captures into the result), and the pieces — placeholder-
// JSON text alternating with real base64 chunks — are handed to `new
// Blob([...])` as separate BlobParts. Blob concatenates them at the BINARY
// level, which has no comparable string-length ceiling (bound by available
// memory, not a fixed character count) — so the combined document never has
// to exist as one JS string at any point. Byte-for-byte identical output to
// a plain `JSON.stringify`, so `importProject`'s reviver needs no changes
// and every already-exported `.ehdz` (local, or sitting in a user's Drive
// folder) still opens exactly the same.
//
// The marker is U+E000, the first Private Use Area code point -- built
// with String.fromCharCode rather than an inline literal, and deliberately
// NOT a control character: JSON.stringify escapes control characters
// (U+0000 through U+001F) into a 6-character escape sequence, which would
// break the split below (it would look for the raw character, while the
// JSON text actually contains its escaped spelling instead). A Private Use
// Area character is printable enough that JSON.stringify passes it through
// untouched, and nothing a real project legitimately contains (names, GIS
// field values...) would ever produce it inside a JSON string -- so the
// placeholder survives into the JSON text as a stable, splittable token.
const CHUNK_MARKER = String.fromCharCode(0xe000);
const CHUNK_PLACEHOLDER_RE = new RegExp(`${CHUNK_MARKER}(\\d+)${CHUNK_MARKER}`, "g");

function chunkedReplacer(chunks: string[]): (key: string, value: unknown) => unknown {
  return (_key, value) => {
    if (value instanceof Map) {
      return { __type: "Map", entries: Array.from(value.entries()) };
    }
    if (value instanceof Float64Array) {
      const bytes = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
      const index = chunks.push(bytesToBase64(bytes)) - 1;
      return { __type: "Float64Array", base64: `${CHUNK_MARKER}${index}${CHUNK_MARKER}` };
    }
    return value;
  };
}

/** Splits `placeholderJson` (produced with `chunkedReplacer`) back into its real BlobParts, substituting each placeholder token for `chunks[n]` — see the comment above `CHUNK_MARKER` for why this two-pass approach exists. */
function blobPartsFromPlaceholderJson(placeholderJson: string, chunks: string[]): BlobPart[] {
  // `.split()` with one capturing group interleaves: [text, digits, text, digits, ..., text].
  const parts = placeholderJson.split(CHUNK_PLACEHOLDER_RE);
  return parts.map((part, i) => (i % 2 === 0 ? part : chunks[Number(part)]));
}

export function reviver(_key: string, value: unknown): unknown {
  if (value && typeof value === "object" && "__type" in value) {
    const v = value as {
      __type: string;
      entries?: [string, unknown][];
      base64?: string;
      values?: number[];
    };
    if (v.__type === "Map") return new Map(v.entries ?? []);
    if (v.__type === "Float64Array") {
      // `base64` is the current format (see chunkedReplacer above); `values`
      // is the old decimal-array format, kept readable so a .ehdz exported
      // before this fix (a local backup, or whatever's already sitting in
      // a user's Drive folder) still imports correctly.
      if (typeof v.base64 === "string") return base64ToFloat64Array(v.base64);
      return new Float64Array(v.values ?? []);
    }
  }
  return value;
}

/**
 * The actual export body: JSON.stringify (chunked, see above) + gzip —
 * everything `exportProject()` used to do directly on the main thread.
 * Synchronous and CPU-heavy up to the `await` (the compression step),
 * exactly why this needs to run inside a Worker for a real-sized project —
 * see this module's own header.
 */
export async function serializeAndCompress(name: string, data: ProjectData): Promise<ArrayBuffer> {
  const wrapper: Wrapper = { format: FORMAT, version: FORMAT_VERSION, name, data };
  const chunks: string[] = [];
  const placeholderJson = JSON.stringify(wrapper, chunkedReplacer(chunks));
  const blobParts = blobPartsFromPlaceholderJson(placeholderJson, chunks);
  const compressedStream = new Blob(blobParts).stream().pipeThrough(new CompressionStream("gzip"));
  return new Response(compressedStream).arrayBuffer();
}
